import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { AI_ASSISTANT_BLUE } from "../constants";
import { isMediaSlide, slideLayout, slideThumb } from "./presentationDraftOps";
import LibraryPicker from "./LibraryPicker";
import { primaryButtonSx, textButtonSx } from "./DraftCardShell";

function SlideThumb({ slide }) {
  const [failed, setFailed] = useState(false);
  const src = slideThumb(slide);
  const is3d = Boolean(slide?.related_3d?.modelUrl);
  return (
    <Box
      sx={{
        width: 56,
        height: 40,
        flexShrink: 0,
        borderRadius: 1.25,
        overflow: "hidden",
        bgcolor: "#f1f5f9",
        border: (t) => `1px solid ${t.palette.line.main}`,
        display: "grid",
        placeItems: "center",
        position: "relative",
      }}
    >
      {src && !failed ? (
        <Box component="img" src={src} alt="" loading="lazy" onError={() => setFailed(true)} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Typography sx={{ fontSize: 9, fontWeight: 800, color: "#94a3b8" }}>TEXT</Typography>
      )}
      {is3d ? (
        <ViewInArRoundedIcon sx={{ position: "absolute", right: 2, bottom: 2, fontSize: 13, color: AI_ASSISTANT_BLUE, bgcolor: "background.card", borderRadius: 0.5 }} />
      ) : null}
    </Box>
  );
}

SlideThumb.propTypes = { slide: PropTypes.object };

const bulletsToText = (bullets) => (Array.isArray(bullets) ? bullets : []).join("\n");

/** Inline editor for one draft slide. Saves straight into the draft card. */
function SlideEditor({ slide, index, disabled, actions, onClose }) {
  const media = isMediaSlide(slide);
  const [title, setTitle] = useState(slide.title || "");
  const [content, setContent] = useState(bulletsToText(slide.bullets));
  const [notes, setNotes] = useState(slide.speaker_notes || "");
  const [picker, setPicker] = useState(null); // "image" | "3d" | null
  const layout = slideLayout(slide);

  const save = () => {
    actions.update(index, {
      title: title.trim() || slide.title,
      bullets: media ? slide.bullets : content.split("\n"),
      speaker_notes: notes,
    });
    onClose();
  };

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <TextField size="small" label="Title" value={title} disabled={disabled} onChange={(e) => setTitle(e.target.value)} fullWidth inputProps={{ maxLength: 140 }} />
      {!media ? (
        <TextField
          size="small"
          label="Educational content (one point per line)"
          value={content}
          disabled={disabled}
          onChange={(e) => setContent(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
        />
      ) : null}
      <TextField
        size="small"
        label="Teaching notes (for you, not shown to students)"
        value={notes}
        disabled={disabled}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
      />

      {!media ? (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", mb: 0.5 }}>LAYOUT</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={layout}
            onChange={(_, value) => {
              if (value === "text") actions.textOnly(index);
              if (value === "visual") setPicker("image");
            }}
            aria-label="Slide layout"
          >
            <ToggleButton value="visual" sx={{ textTransform: "none", fontSize: 12, px: 1.25 }}>
              Text + visual
            </ToggleButton>
            <ToggleButton value="text" sx={{ textTransform: "none", fontSize: 12, px: 1.25 }}>
              Text only
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : null}

      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", mb: 0.5 }}>VISUALS</Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
          {slide.supporting_image?.url || (slide.related_3d && !slide.related_3d.modelUrl) ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={disabled}
              startIcon={<ImageOutlinedIcon fontSize="small" />}
              onClick={() => actions.clearImage(index)}
              sx={textButtonSx}
            >
              Remove image
            </Button>
          ) : null}
          {slide.related_3d?.modelUrl ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={disabled}
              startIcon={<ViewInArRoundedIcon fontSize="small" />}
              onClick={() => actions.clear3d(index)}
              sx={textButtonSx}
            >
              Remove 3D model
            </Button>
          ) : null}
          <Button size="small" disabled={disabled} onClick={() => setPicker(picker === "image" ? null : "image")} sx={textButtonSx}>
            {slide.supporting_image?.url || slide.related_3d ? "Replace image" : "Add histology image"}
          </Button>
          <Button size="small" disabled={disabled} onClick={() => setPicker(picker === "3d" ? null : "3d")} sx={textButtonSx}>
            {slide.related_3d?.modelUrl ? "Replace 3D model" : "Add 3D model"}
          </Button>
        </Stack>
        {slide.related_3d?.label ? (
          <Typography sx={{ mt: 0.4, fontSize: 11.5, color: "ink.soft" }}>
            On this slide: <strong>{slide.related_3d.label}</strong>
          </Typography>
        ) : null}
        {picker ? (
          <Box sx={{ mt: 0.75 }}>
            <LibraryPicker
              autoFocus
              filter={picker}
              disabled={disabled}
              onSearch={actions.search}
              placeholder={picker === "3d" ? "Search 3D models: chondrocyte, neuron…" : "Search histology: hyaline cartilage…"}
              actions={[
                {
                  label: "Use on this slide",
                  onPick: (item) => {
                    actions.setVisual(index, item);
                    setPicker(null);
                  },
                },
              ]}
            />
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={0.75}>
        <Button size="small" variant="contained" disableElevation disabled={disabled} onClick={save} sx={primaryButtonSx}>
          Save slide
        </Button>
        <Button size="small" disabled={disabled} onClick={onClose} sx={textButtonSx}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

SlideEditor.propTypes = {
  slide: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  actions: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function SlideCard({ slide, index, total, disabled, actions, startEditing = false }) {
  const [mode, setMode] = useState(startEditing ? "edit" : null); // null | "preview" | "edit" | "replace"
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [instruction, setInstruction] = useState("");
  const media = isMediaSlide(slide);
  const bullets = Array.isArray(slide.bullets) ? slide.bullets : [];

  useEffect(() => {
    if (startEditing) setMode("edit");
  }, [startEditing]);

  const close = () => setMode(null);

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 2,
        bgcolor: mode === "edit" ? "#f8fbff" : "#ffffff",
        border: "1px solid",
        borderColor: mode === "edit" ? "rgba(0,97,254,0.45)" : media ? "rgba(0,97,254,0.22)" : "rgba(148,163,184,0.3)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ width: 18, flexShrink: 0, fontSize: 12, fontWeight: 800, color: AI_ASSISTANT_BLUE, textAlign: "center" }}>
          {index + 1}
        </Typography>
        <SlideThumb slide={slide} />
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setMode(mode === "preview" ? null : "preview")}
          onKeyDown={(e) => {
            if (e.key === "Enter") setMode(mode === "preview" ? null : "preview");
          }}
          sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
          aria-label={`Preview slide ${index + 1}`}
        >
          <Typography noWrap fontWeight={700} sx={{ fontSize: 13, color: "ink.strong" }}>
            {slide.title || `Slide ${index + 1}`}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: "#64748b" }}>
            {media
              ? `${slide.related_3d?.modelUrl ? "3D model" : "Library slide"} · full visual`
              : bullets.length
                ? bullets.join(" · ")
                : "No content yet"}
          </Typography>
        </Box>
        <Tooltip title="Edit">
          <span>
            <IconButton size="small" aria-label={`Edit slide ${index + 1}`} disabled={disabled} onClick={() => setMode(mode === "edit" ? null : "edit")} sx={{ color: AI_ASSISTANT_BLUE }}>
              <EditRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete from draft">
          <span>
            <IconButton
              size="small"
              aria-label={`Delete slide ${index + 1}`}
              disabled={disabled || total <= 1}
              onClick={() => actions.remove(index)}
              sx={{ color: "#b91c1c" }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" aria-label={`More actions for slide ${index + 1}`} disabled={disabled} onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreHorizRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMode("preview"); setMenuAnchor(null); }}>Preview</MenuItem>
        <MenuItem disabled={index === 0} onClick={() => { actions.move(index, index - 1); setMenuAnchor(null); }}>
          <ArrowUpwardRoundedIcon sx={{ fontSize: 16, mr: 1 }} /> Move up
        </MenuItem>
        <MenuItem disabled={index >= total - 1} onClick={() => { actions.move(index, index + 1); setMenuAnchor(null); }}>
          <ArrowDownwardRoundedIcon sx={{ fontSize: 16, mr: 1 }} /> Move down
        </MenuItem>
        <MenuItem onClick={() => { actions.duplicate(index); setMenuAnchor(null); }}>Duplicate</MenuItem>
        <MenuItem onClick={() => { actions.addAfter(index); setMenuAnchor(null); }}>Add slide after</MenuItem>
        {!media ? (
          <MenuItem onClick={() => { setMode("replace"); setMenuAnchor(null); }}>Replace content with AI…</MenuItem>
        ) : null}
      </Menu>

      {mode === "preview" ? (
        <Box sx={{ mt: 1, ml: 3.25, p: 1.25, borderRadius: 1.5, bgcolor: "background.elevated", border: (t) => `1px solid ${t.palette.line.main}` }}>
          <Typography fontWeight={700} sx={{ fontSize: 13.5, color: "ink.strong" }}>{slide.title}</Typography>
          {bullets.length ? (
            <Box component="ul" sx={{ m: "6px 0 0", pl: 2.2 }}>
              {bullets.map((b, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography component="li" key={i} sx={{ fontSize: 12.5, color: "ink.body", mb: 0.3 }}>{b}</Typography>
              ))}
            </Box>
          ) : null}
          {slideThumb(slide) ? (
            <Box component="img" src={slideThumb(slide)} alt={slide.related_3d?.label || ""} sx={{ mt: 1, width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1.25 }} />
          ) : null}
          {slide.related_3d?.label ? (
            <Typography sx={{ mt: 0.5, fontSize: 11.5, color: "ink.soft" }}>
              {slide.related_3d.modelUrl ? "3D model" : "Library slide"}: {slide.related_3d.label}
            </Typography>
          ) : null}
          {slide.speaker_notes ? (
            <Typography sx={{ mt: 0.75, fontSize: 11.5, color: "#64748b", fontStyle: "italic" }}>Notes: {slide.speaker_notes}</Typography>
          ) : null}
        </Box>
      ) : null}

      {mode === "edit" ? (
        <SlideEditor slide={slide} index={index} disabled={disabled} actions={actions} onClose={close} />
      ) : null}

      {mode === "replace" ? (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            label="How should the AI rewrite this slide?"
            placeholder="e.g. simpler for first-year students"
            value={instruction}
            disabled={disabled}
            onChange={(e) => setInstruction(e.target.value)}
          />
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={disabled}
              onClick={() => {
                actions.rewrite(index, instruction);
                setInstruction("");
                close();
              }}
              sx={primaryButtonSx}
            >
              Rewrite slide
            </Button>
            <Button size="small" onClick={close} sx={textButtonSx}>Cancel</Button>
          </Stack>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>This sends a chat message and counts toward the limit.</Typography>
        </Stack>
      ) : null}
    </Box>
  );
}

SlideCard.propTypes = {
  slide: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  actions: PropTypes.shape({
    update: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    duplicate: PropTypes.func.isRequired,
    move: PropTypes.func.isRequired,
    addAfter: PropTypes.func.isRequired,
    setVisual: PropTypes.func.isRequired,
    clearImage: PropTypes.func.isRequired,
    clear3d: PropTypes.func.isRequired,
    textOnly: PropTypes.func.isRequired,
    rewrite: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
  }).isRequired,
  startEditing: PropTypes.bool,
};
